import React, { useEffect, useMemo, useState } from 'react';
import { db, storage } from '../../firebase/config';
import {
  collection,
  addDoc,
  serverTimestamp,
  doc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  onSnapshot
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../../context/AuthContext';
import { signOutUser } from '../../services/authService';
import Login from '../Auth/Login';
import AdminContacts from './AdminContacts';
import logo from '../../../public/assets/images/logo.png';

const NAV_ITEMS = [
  { id: 'overview', label: 'Overview', icon: 'grid' },
  { id: 'import', label: 'Import Tour files', icon: 'upload' },
  { id: 'tours', label: 'Manage Tours', icon: 'route' },
  { id: 'users', label: 'Manage Users', icon: 'users' },
  { id: 'contacts', label: 'Messages', icon: 'message' }
];

function Icon({ name, size = 18, strokeWidth = 1.8, className = '' }) {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    className
  };

  const paths = {
    grid: (
      <>
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
      </>
    ),
    upload: (
      <>
        <path d="M12 16V4" />
        <path d="m7 9 5-5 5 5" />
        <path d="M5 20h14" />
      </>
    ),
    route: (
      <>
        <circle cx="6" cy="6" r="2.5" />
        <circle cx="18" cy="18" r="2.5" />
        <path d="M8.5 6H11a5 5 0 0 1 5 5v2" />
        <path d="M16 18h-2.5a5 5 0 0 1-5-5v-2" />
      </>
    ),
    users: (
      <>
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </>
    ),
    message: (
      <>
        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7A8.38 8.38 0 0 1 4 11.5a8.5 8.5 0 1 1 17 0Z" />
        <path d="M8 12h.01M12 12h.01M16 12h.01" />
      </>
    ),
    search: (
      <>
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-4-4" />
      </>
    ),
    bell: (
      <>
        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
        <path d="M10 21h4" />
      </>
    ),
    settings: (
      <>
        <path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z" />
        <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06-1.42 1.42-.06-.06a1.7 1.7 0 0 0-1.88-.34 1.7 1.7 0 0 0-1.03 1.56V20h-2v-.09a1.7 1.7 0 0 0-1.03-1.56 1.7 1.7 0 0 0-1.88.34l-.06.06-1.42-1.42.06-.06A1.7 1.7 0 0 0 9.4 15a1.7 1.7 0 0 0-1.56-1.03H7.75v-2h.09A1.7 1.7 0 0 0 9.4 10a1.7 1.7 0 0 0-.34-1.88L9 8.06l1.42-1.42.06.06a1.7 1.7 0 0 0 1.88.34A1.7 1.7 0 0 0 13.39 5.5V5h2v.5a1.7 1.7 0 0 0 1.03 1.54 1.7 1.7 0 0 0 1.88-.34l.06-.06 1.42 1.42-.06.06A1.7 1.7 0 0 0 19.4 10c.2.62.78 1.03 1.44 1.03H21v2h-.16A1.7 1.7 0 0 0 19.4 15Z" />
      </>
    ),
    logout: (
      <>
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
        <path d="m16 17 5-5-5-5" />
        <path d="M21 12H9" />
      </>
    ),
    plus: (
      <>
        <path d="M12 5v14M5 12h14" />
      </>
    ),
    arrow: (
      <>
        <path d="M5 12h14" />
        <path d="m13 6 6 6-6 6" />
      </>
    ),
    star: (
      <path d="m12 3 2.78 5.63 6.22.9-4.5 4.39 1.06 6.2L12 17.2l-5.56 2.92 1.06-6.2L3 9.53l6.22-.9L12 3Z" />
    ),
    clock: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </>
    ),
    file: (
      <>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
        <path d="M14 2v6h6M8 13h8M8 17h5" />
      </>
    ),
    check: (
      <path d="m5 12 4 4L19 6" />
    ),
    trash: (
      <>
        <path d="M3 6h18" />
        <path d="M8 6V4h8v2M19 6l-1 15H6L5 6" />
        <path d="M10 11v6M14 11v6" />
      </>
    ),
    edit: (
      <>
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L8 18l-4 1 1-4Z" />
      </>
    ),
    filter: (
      <>
        <path d="M4 6h16M7 12h10M10 18h4" />
      </>
    ),
    menu: (
      <>
        <path d="M4 6h16M4 12h16M4 18h16" />
      </>
    ),
    close: (
      <>
        <path d="m6 6 12 12M18 6 6 18" />
      </>
    ),
    chevron: (
      <path d="m7 10 5 5 5-5" />
    ),
    image: (
      <>
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <path d="m21 15-5-5L5 21" />
      </>
    ),
    shield: (
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
    ),
    mail: (
      <>
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="m3 7 9 6 9-6" />
      </>
    )
  };

  return <svg {...common}>{paths[name] || paths.grid}</svg>;
}

const pageMeta = {
  overview: {
    title: 'Overview',
    description: 'A quick view of your tourism platform.'
  },
  import: {
    title: 'Import Tour files',
    description: 'Upload tour images and itinerary documents.'
  },
  tours: {
    title: 'Manage Tours',
    description: 'Create, review and manage your safari tours.'
  },
  users: {
    title: 'Manage Users',
    description: 'Manage platform users and administrator access.'
  },
  contacts: {
    title: 'Messages',
    description: 'Review enquiries and contact form submissions.'
  }
};

const formatDate = (value) => {
  if (!value) return 'Never';
  try {
    if (value?.seconds) return new Date(value.seconds * 1000).toLocaleDateString();
    return new Date(value).toLocaleDateString();
  } catch {
    return 'Never';
  }
};

const formatCurrency = (value) =>
  `N$${Number(value || 0).toLocaleString('en-NA', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })}`;

const extractFilenameFromUrl = (url) => {
  if (!url) return '';
  if (!url.startsWith('http')) return url;
  try {
    const match = url.match(/\/o\/([^?]+)/);
    if (match && match[1]) {
      return decodeURIComponent(match[1]).split('/').pop();
    }
    return decodeURIComponent(url.split('?')[0].split('/').pop());
  } catch {
    return url;
  }
};

const ImportTours = () => {
  const { user, isAuthorized, loading: authLoading } = useAuth();

  // Files keyed by tour ID
  const [selectedImages, setSelectedImages] = useState({});
  const [selectedPdfs, setSelectedPdfs] = useState({});
  const [importLoading, setImportLoading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, message: '' });
  const [results, setResults] = useState({ success: [], errors: [] });

  const [activePage, setActivePage] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loadingError, setLoadingError] = useState(null);

  const [stats, setStats] = useState({
    totalTours: 0,
    featuredTours: 0,
    totalUsers: 0,
    pendingImports: 0,
    newContacts: 0
  });
  const [statsLoading, setStatsLoading] = useState(true);

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [userMessage, setUserMessage] = useState('');

  const [existingTours, setExistingTours] = useState([]);
  const [toursLoading, setToursLoading] = useState(true);
  const [tourMessage, setTourMessage] = useState('');
  const [showAddTourForm, setShowAddTourForm] = useState(false);
  const [newTour, setNewTour] = useState({
    title: '',
    description: '',
    duration: '',
    price: '',
    rating: '',
    featured: false
  });
  const [newTourImage, setNewTourImage] = useState(null);
  const [newTourPdf, setNewTourPdf] = useState(null);
  const [isAddingTour, setIsAddingTour] = useState(false);

  const [uploadingTourId, setUploadingTourId] = useState(null);

  const [editingTour, setEditingTour] = useState(null);
  const [editTourImage, setEditTourImage] = useState(null);
  const [editTourPdf, setEditTourPdf] = useState(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const [tourSearch, setTourSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');

  useEffect(() => {
    if (!isAuthorized) return undefined;

    const timeout = setTimeout(() => {
      if (statsLoading) {
        setStatsLoading(false);
        setLoadingError(
          'Loading is taking longer than expected. Please refresh the page.'
        );
      }
    }, 10000);

    return () => clearTimeout(timeout);
  }, [isAuthorized, statsLoading]);

  useEffect(() => {
    if (!isAuthorized) return undefined;

    let isMounted = true;
    let unsubscribeTours = () => { };
    let unsubscribeUsers = () => { };
    let unsubscribeContacts = () => { };

    try {
      unsubscribeTours = onSnapshot(
        collection(db, 'tours'),
        (snapshot) => {
          if (!isMounted) return;
          let featured = 0;
          snapshot.forEach((item) => {
            if (item.data().featured === true) featured++;
          });
          setStats((prev) => ({
            ...prev,
            totalTours: snapshot.size,
            featuredTours: featured
          }));
        },
        (error) => {
          console.error('Error fetching tours:', error);
          if (isMounted) {
            setStatsLoading(false);
            setLoadingError(
              'Failed to load tours. Please check your Firebase connection.'
            );
          }
        }
      );

      unsubscribeUsers = onSnapshot(
        collection(db, 'users'),
        (snapshot) => {
          if (!isMounted) return;
          setStats((prev) => ({ ...prev, totalUsers: snapshot.size }));
          setStatsLoading(false);
        },
        (error) => {
          console.error('Error fetching users:', error);
          if (isMounted) {
            setStatsLoading(false);
            setLoadingError(
              'Failed to load users. Please check your Firebase connection.'
            );
          }
        }
      );

      unsubscribeContacts = onSnapshot(
        collection(db, 'contacts'),
        (snapshot) => {
          if (!isMounted) return;
          let newCount = 0;
          snapshot.forEach((item) => {
            if (item.data().status === 'new') newCount++;
          });
          setStats((prev) => ({ ...prev, newContacts: newCount }));
        },
        (error) => {
          console.error('Error fetching contacts:', error);
        }
      );
    } catch (error) {
      console.error('Error setting up listeners:', error);
      if (isMounted) {
        setStatsLoading(false);
        setLoadingError(
          'Failed to connect to Firebase. Please refresh and try again.'
        );
      }
    }

    return () => {
      isMounted = false;
      unsubscribeTours();
      unsubscribeUsers();
      unsubscribeContacts();
    };
  }, [isAuthorized]);

  useEffect(() => {
    if (!isAuthorized) return undefined;

    let isMounted = true;
    let unsubscribe = () => { };

    try {
      unsubscribe = onSnapshot(
        query(collection(db, 'users'), orderBy('createdAt', 'desc')),
        (snapshot) => {
          if (!isMounted) return;
          const usersData = [];
          snapshot.forEach((item) => {
            usersData.push({ id: item.id, ...item.data() });
          });
          setUsers(usersData);
          setUsersLoading(false);
        },
        (error) => {
          console.error('Error fetching users:', error);
          if (isMounted) setUsersLoading(false);
        }
      );
    } catch (error) {
      console.error('Error setting up users listener:', error);
      if (isMounted) setUsersLoading(false);
    }

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [isAuthorized]);

  useEffect(() => {
    if (!isAuthorized) return undefined;

    let isMounted = true;
    let unsubscribe = () => { };

    try {
      unsubscribe = onSnapshot(
        query(collection(db, 'tours'), orderBy('createdAt', 'desc')),
        (snapshot) => {
          if (!isMounted) return;
          const toursData = [];
          snapshot.forEach((item) => {
            toursData.push({ id: item.id, ...item.data() });
          });
          setExistingTours(toursData);
          setToursLoading(false);
        },
        (error) => {
          console.error('Error fetching tours:', error);
          if (isMounted) setToursLoading(false);
        }
      );
    } catch (error) {
      console.error('Error setting up tours listener:', error);
      if (isMounted) setToursLoading(false);
    }

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [isAuthorized]);

  const changePage = (page) => {
    setActivePage(page);
    setSidebarOpen(false);
  };

  const uploadFile = async (file, folder, fileName) => {
    if (!file) return null;

    try {
      const storedName = `${Date.now()}_${fileName}`;
      const storageRef = ref(storage, `${folder}/${storedName}`);
      const snapshot = await uploadBytes(storageRef, file);
      const url = await getDownloadURL(snapshot.ref);

      return { url, filename: storedName };
    } catch (error) {
      console.error('Upload error:', error);
      throw new Error(`Failed to upload ${fileName}: ${error.message}`);
    }
  };

  // Files keyed by tour ID
  const handleImageSelect = (tourId, file) => {
    setSelectedImages((prev) => ({ ...prev, [tourId]: file }));
  };

  const handlePdfSelect = (tourId, file) => {
    setSelectedPdfs((prev) => ({ ...prev, [tourId]: file }));
  };

  // Iterate over tours from the DB — only update ones with newly picked files
  const importAllTours = async () => {
    const targets = existingTours.filter(
      (tour) => selectedImages[tour.id] || selectedPdfs[tour.id]
    );

    if (targets.length === 0) {
      setTourMessage('Pick a file for at least one tour before saving.');
      setTimeout(() => setTourMessage(''), 3000);
      return;
    }

    setImportLoading(true);
    setResults({ success: [], errors: [] });

    for (let i = 0; i < targets.length; i++) {
      const tour = targets[i];

      setProgress({
        current: i + 1,
        total: targets.length,
        message: `Processing ${tour.title}`
      });

      try {
        let newCover = null;
        if (selectedImages[tour.id]) {
          newCover = await uploadFile(
            selectedImages[tour.id],
            'tours/covers',
            selectedImages[tour.id].name
          );
        }

        let newPdf = null;
        if (selectedPdfs[tour.id]) {
          newPdf = await uploadFile(
            selectedPdfs[tour.id],
            'tours/itineraries',
            selectedPdfs[tour.id].name
          );
        }

        const updateData = { updatedAt: serverTimestamp() };
        if (newCover) updateData.coverImage = newCover.url;
        if (newPdf) updateData.itineraryPdf = newPdf.filename;

        await updateDoc(doc(db, 'tours', tour.id), updateData);

        setResults((prev) => ({
          ...prev,
          success: [
            ...prev.success,
            { title: tour.title, id: tour.id, updated: true }
          ]
        }));
      } catch (error) {
        console.error(`Error processing ${tour.title}:`, error);
        setResults((prev) => ({
          ...prev,
          errors: [
            ...prev.errors,
            { title: tour.title, error: error.message }
          ]
        }));
      }
    }

    setImportLoading(false);
    setProgress({ current: 0, total: 0, message: 'Save complete' });
    setSelectedImages({});
    setSelectedPdfs({});
  };

  const uploadItineraryForTour = async (tourId, file) => {
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setTourMessage('Please select a PDF file.');
      setTimeout(() => setTourMessage(''), 3000);
      return;
    }

    setUploadingTourId(tourId);
    setTourMessage('');

    try {
      const uploaded = await uploadFile(file, 'tours/itineraries', file.name);
      if (!uploaded) throw new Error('Upload failed');

      await updateDoc(doc(db, 'tours', tourId), {
        itineraryPdf: uploaded.filename,
        updatedAt: serverTimestamp()
      });

      setTourMessage(`Itinerary uploaded: ${uploaded.filename}`);
      setTimeout(() => setTourMessage(''), 4000);
    } catch (error) {
      console.error('Itinerary upload failed:', error);
      setTourMessage(`Error: ${error.message}`);
      setTimeout(() => setTourMessage(''), 4000);
    } finally {
      setUploadingTourId(null);
    }
  };

  const openEditTour = (tour) => {
    setEditingTour({
      id: tour.id,
      title: tour.title || '',
      description: tour.description || '',
      duration: tour.duration || '',
      price: tour.price ?? '',
      rating: tour.rating ?? '',
      featured: Boolean(tour.featured),
      coverImage: tour.coverImage || '',
      itineraryPdf: tour.itineraryPdf || ''
    });
    setEditTourImage(null);
    setEditTourPdf(null);
    setTourMessage('');
  };

  const closeEditTour = () => {
    if (isSavingEdit) return;
    setEditingTour(null);
    setEditTourImage(null);
    setEditTourPdf(null);
  };

  const handleUpdateTour = async (event) => {
    event.preventDefault();
    if (!editingTour) return;

    if (!editingTour.title || !editingTour.duration || editingTour.price === '') {
      setTourMessage('Title, duration and price are required.');
      setTimeout(() => setTourMessage(''), 3000);
      return;
    }

    setIsSavingEdit(true);
    setTourMessage('');

    try {
      let newCover = null;
      if (editTourImage) {
        newCover = await uploadFile(editTourImage, 'tours/covers', editTourImage.name);
      }

      let newPdf = null;
      if (editTourPdf) {
        newPdf = await uploadFile(editTourPdf, 'tours/itineraries', editTourPdf.name);
      }

      const updateData = {
        title: editingTour.title.trim(),
        description: editingTour.description.trim() || '',
        duration: editingTour.duration.trim(),
        price: parseFloat(editingTour.price) || 0,
        rating: parseFloat(editingTour.rating) || 0,
        featured: Boolean(editingTour.featured),
        updatedAt: serverTimestamp()
      };

      if (newCover) updateData.coverImage = newCover.url;
      if (newPdf) updateData.itineraryPdf = newPdf.filename;

      await updateDoc(doc(db, 'tours', editingTour.id), updateData);

      setTourMessage(`Tour "${editingTour.title}" updated.`);
      setEditingTour(null);
      setEditTourImage(null);
      setEditTourPdf(null);
      setTimeout(() => setTourMessage(''), 4000);
    } catch (error) {
      console.error('Update tour failed:', error);
      setTourMessage(`Error: ${error.message}`);
      setTimeout(() => setTourMessage(''), 4000);
    } finally {
      setIsSavingEdit(false);
    }
  };

  const toggleAdminRole = async (uid, currentRole) => {
    try {
      const newRole = currentRole === 'admin' ? 'user' : 'admin';
      await updateDoc(doc(db, 'users', uid), {
        role: newRole,
        isAdmin: newRole === 'admin',
        updatedAt: serverTimestamp()
      });
      setUserMessage(`User role updated to ${newRole}`);
      setTimeout(() => setUserMessage(''), 3000);
    } catch (error) {
      setUserMessage(`Error: ${error.message}`);
      setTimeout(() => setUserMessage(''), 3000);
    }
  };

  const deleteUser = async (uid) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await deleteDoc(doc(db, 'users', uid));
      setUserMessage('User deleted successfully');
      setTimeout(() => setUserMessage(''), 3000);
    } catch (error) {
      setUserMessage(`Error: ${error.message}`);
      setTimeout(() => setUserMessage(''), 3000);
    }
  };

  const deleteTour = async (tourId) => {
    if (!window.confirm('Are you sure you want to delete this tour?')) return;

    try {
      await deleteDoc(doc(db, 'tours', tourId));
      setTourMessage('Tour deleted successfully');
      setTimeout(() => setTourMessage(''), 3000);
    } catch (error) {
      setTourMessage(`Error: ${error.message}`);
      setTimeout(() => setTourMessage(''), 3000);
    }
  };

  const handleAddTour = async (event) => {
    event.preventDefault();

    if (!newTour.title || !newTour.duration || !newTour.price) {
      setTourMessage('Please fill in all required fields: title, duration and price.');
      setTimeout(() => setTourMessage(''), 3000);
      return;
    }

    setIsAddingTour(true);
    setTourMessage('');

    try {
      let newCover = null;
      if (newTourImage) {
        newCover = await uploadFile(newTourImage, 'tours/covers', newTourImage.name);
      }

      let newPdf = null;
      if (newTourPdf) {
        newPdf = await uploadFile(newTourPdf, 'tours/itineraries', newTourPdf.name);
      }

      await addDoc(collection(db, 'tours'), {
        title: newTour.title.trim(),
        description: newTour.description.trim() || '',
        duration: newTour.duration.trim(),
        price: parseFloat(newTour.price) || 0,
        rating: parseFloat(newTour.rating) || 0,
        featured: newTour.featured || false,
        coverImage: newCover?.url || '',
        itineraryPdf: newPdf?.filename || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      setTourMessage(`Tour "${newTour.title}" added successfully.`);
      setShowAddTourForm(false);

      setNewTour({
        title: '',
        description: '',
        duration: '',
        price: '',
        rating: '',
        featured: false
      });
      setNewTourImage(null);
      setNewTourPdf(null);

      document.querySelectorAll('input[type="file"]').forEach((input) => {
        input.value = '';
      });

      setTimeout(() => setTourMessage(''), 4000);
    } catch (error) {
      console.error('Error adding tour:', error);
      setTourMessage(`Error: ${error.message}`);
      setTimeout(() => setTourMessage(''), 4000);
    } finally {
      setIsAddingTour(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOutUser();
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const filteredTours = useMemo(() => {
    const search = tourSearch.trim().toLowerCase();
    if (!search) return existingTours;
    return existingTours.filter((tour) =>
      [tour.title, tour.description, tour.duration]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(search))
    );
  }, [existingTours, tourSearch]);

  const filteredUsers = useMemo(() => {
    const search = userSearch.trim().toLowerCase();
    if (!search) return users;
    return users.filter((item) =>
      [item.displayName, item.email, item.role]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(search))
    );
  }, [users, userSearch]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#f5f6f8] flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-10 w-10 rounded-full border-4 border-slate-200 border-t-violet-600 animate-spin" />
          <p className="mt-4 text-sm font-medium text-slate-700">Authenticating...</p>
          <p className="mt-1 text-xs text-slate-400">
            Preparing your administration workspace
          </p>
        </div>
      </div>
    );
  }

  if (!user || !isAuthorized) {
    return <Login />;
  }

  if (loadingError) {
    return (
      <div className="min-h-screen bg-[#f5f6f8] flex items-center justify-center p-6">
        <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-sm border border-slate-200">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-500">
            <Icon name="close" size={22} />
          </div>

          <span className="mt-5 text-lg font-semibold text-slate-900">
            Connection problem
          </span>

          <p className="mt-2 text-sm leading-6 text-slate-500">{loadingError}</p>

          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-6 inline-flex items-center justify-center rounded-xl bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-700"
          >
            Refresh page
          </button>
        </div>
      </div>
    );
  }

  const currentPage = pageMeta[activePage];

  return (
    <div className="min-h-screen bg-[#f5f6f8] text-slate-900">
      {sidebarOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          onClick={() => setSidebarOpen(false)}
          className="fixed inset-0 z-40 bg-slate-950/30 lg:hidden"
        />
      )}

      <aside
        className={[
          'fixed inset-y-0 left-0 z-50 flex w-[248px] flex-col border-r border-slate-200 bg-white transition-transform duration-200',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        ].join(' ')}
      >
        <div className="flex  items-center border-b border-slate-100 px-5">
          <div className="mb-6 flex items-center justify-center ">
            <img
              style={{ height: '100px', transition: 'all 0.5s ease' }}
              src={logo}
              alt="legendLogo"
            />
          </div>

          <button
            type="button"
            onClick={() => setSidebarOpen(false)}
            className="ml-auto rounded-lg p-2 text-slate-400 hover:bg-slate-50 hover:text-slate-700 lg:hidden"
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-5">
          <p className="px-3 pb-2 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">
            General Menu
          </p>

          <div className="space-y-1">
            {NAV_ITEMS.map((item) => {
              const active = activePage === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => changePage(item.id)}
                  className={[
                    'group flex w-full items-center rounded-xl px-3 py-2.5 text-left text-[13px] font-medium transition',
                    active
                      ? 'bg-violet-50 text-violet-700'
                      : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'
                  ].join(' ')}
                >
                  <Icon
                    name={item.icon}
                    size={10}
                    className={[
                      'mr-3 shrink-0',
                      active ? 'text-violet-600' : 'text-slate-400 group-hover:text-slate-600'
                    ].join(' ')}
                  />

                  <span className="flex-1">{item.label}</span>

                  {item.id === 'contacts' && stats.newContacts > 0 && (
                    <span className="min-w-5 rounded-full bg-red-500 px-1.5 py-0.5 text-center text-[10px] font-bold text-white">
                      {stats.newContacts > 99 ? '99+' : stats.newContacts}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="my-6 h-px bg-slate-100" />

          <p className="px-3 pb-2 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">
            Other Menu
          </p>

          <button
            type="button"
            className="flex w-full items-center rounded-xl px-3 py-2.5 text-left text-[13px] font-medium text-slate-500 hover:bg-slate-50 hover:text-slate-900"
          >
            <Icon name="settings" size={10} className="mr-3 text-slate-400" />
            Settings
          </button>

          <div className="mt-5 rounded-2xl bg-slate-50 p-3.5">
            <div className="flex items-center">
              <span className="mr-2 h-2 w-2 rounded-full bg-emerald-500" />
              <span className="text-xs font-semibold text-slate-700">System Online</span>
            </div>

            <p className="mt-1 text-[11px] leading-5 text-slate-400">
              Firebase services are connected and operational.
            </p>
          </div>
        </nav>

        <div className="border-t border-slate-100 p-3">
          <button
            type="button"
            onClick={handleSignOut}
            className="flex w-full items-center rounded-xl px-3 py-2.5 text-left text-[13px] font-medium text-slate-500 transition hover:bg-red-50 hover:text-red-600"
          >
            <Icon name="logout" size={17} className="mr-3" />
            Sign Out
          </button>
        </div>
      </aside>

      <div className="lg:pl-[248px]">
        <header className="sticky top-0 z-30 flex h-[76px] items-center justify-between border-b border-slate-200 bg-white/95 px-4 backdrop-blur md:px-7">
          <div className="flex min-w-0 items-center">
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="mr-3 rounded-xl border border-slate-200 p-2 text-slate-500 hover:bg-slate-50 lg:hidden"
            >
              <Icon name="menu" size={19} />
            </button>

            <div className="min-w-0">
              <span className="truncate text-lg font-bold text-slate-900">
                {currentPage.title}
              </span>
              <p className="hidden text-xs text-slate-400 sm:block">
                {currentPage.description}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 md:gap-4">
            <button
              type="button"
              className="hidden rounded-xl p-2 text-slate-500 hover:bg-slate-50 sm:block"
              aria-label="Settings"
            >
              <Icon name="settings" size={18} />
            </button>

            <button
              type="button"
              className="relative rounded-xl p-2 text-slate-500 hover:bg-slate-50"
              aria-label="Notifications"
            >
              <Icon name="bell" size={18} />
              {stats.newContacts > 0 && (
                <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-red-500" />
              )}
            </button>

            <div className="hidden h-7 w-px bg-slate-200 sm:block" />

            <div className="flex items-center">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-violet-100 text-sm font-bold text-violet-700">
                {user?.email?.charAt(0).toUpperCase() || 'A'}
              </div>

              <div className="ml-2 hidden max-w-[150px] md:block">
                <p className="truncate text-xs font-semibold text-slate-800">
                  Administrator
                </p>
                <p className="truncate text-[10px] text-slate-400">{user?.email}</p>
              </div>

              <Icon name="chevron" size={14} className="ml-2 text-slate-400" />
            </div>
          </div>
        </header>

        <main className="mx-auto max-w-[1450px] p-4 md:p-6 xl:p-8">
          {activePage === 'overview' && (
            <OverviewPage
              stats={stats}
              user={user}
              changePage={changePage}
              statsLoading={statsLoading}
            />
          )}

          {activePage === 'import' && (
            <ImportPage
              tours={existingTours}
              toursLoading={toursLoading}
              selectedImages={selectedImages}
              selectedPdfs={selectedPdfs}
              handleImageSelect={handleImageSelect}
              handlePdfSelect={handlePdfSelect}
              importAllTours={importAllTours}
              importLoading={importLoading}
              progress={progress}
              results={results}
            />
          )}

          {activePage === 'tours' && (
            <ToursPage
              tours={filteredTours}
              toursLoading={toursLoading}
              search={tourSearch}
              setSearch={setTourSearch}
              showAddTourForm={showAddTourForm}
              setShowAddTourForm={setShowAddTourForm}
              newTour={newTour}
              setNewTour={setNewTour}
              newTourImage={newTourImage}
              setNewTourImage={setNewTourImage}
              newTourPdf={newTourPdf}
              setNewTourPdf={setNewTourPdf}
              handleAddTour={handleAddTour}
              isAddingTour={isAddingTour}
              deleteTour={deleteTour}
              tourMessage={tourMessage}
              uploadItineraryForTour={uploadItineraryForTour}
              uploadingTourId={uploadingTourId}
              openEditTour={openEditTour}
            />
          )}

          {activePage === 'users' && (
            <UsersPage
              users={filteredUsers}
              usersLoading={usersLoading}
              search={userSearch}
              setSearch={setUserSearch}
              toggleAdminRole={toggleAdminRole}
              deleteUser={deleteUser}
              userMessage={userMessage}
            />
          )}

          {activePage === 'contacts' && (
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <AdminContacts />
            </div>
          )}
        </main>
      </div>

      {editingTour && (
        <EditTourModal
          editingTour={editingTour}
          setEditingTour={setEditingTour}
          editTourImage={editTourImage}
          setEditTourImage={setEditTourImage}
          editTourPdf={editTourPdf}
          setEditTourPdf={setEditTourPdf}
          handleUpdateTour={handleUpdateTour}
          isSavingEdit={isSavingEdit}
          closeEditTour={closeEditTour}
        />
      )}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Overview                                                                    */
/* -------------------------------------------------------------------------- */

function OverviewPage({ stats, changePage }) {
  const cards = [
    {
      label: 'Total Tours',
      value: stats.totalTours,
      description: 'Tours in the system',
      icon: 'route',
      tone: 'violet',
      action: 'tours'
    },
    {
      label: 'Featured Tours',
      value: stats.featuredTours,
      description: 'Currently featured',
      icon: 'star',
      tone: 'amber',
      action: 'tours'
    },
    {
      label: 'Registered Users',
      value: stats.totalUsers,
      description: 'Platform users',
      icon: 'users',
      tone: 'emerald',
      action: 'users'
    },
    {
      label: 'New Messages',
      value: stats.newContacts,
      description: 'Unread enquiries',
      icon: 'message',
      tone: 'blue',
      action: 'contacts'
    }
  ];

  const toneClasses = {
    violet: 'bg-violet-50 text-violet-600',
    amber: 'bg-amber-50 text-amber-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    blue: 'bg-blue-50 text-blue-600'
  };

  return (
    <div>
      <div className="mb-6 flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <div>
          <p className="text-sm text-slate-400">Good morning</p>
          <span className="mt-1 text-2xl font-bold tracking-tight text-slate-900 md:text-[28px]">
            Hello, Administrator <span className="text-xl">👋</span>
          </span>
          <p className="mt-1 text-sm text-slate-500">
            Here's a quick overview of your safari platform.
          </p>
        </div>

        <button
          type="button"
          onClick={() => changePage('tours')}
          className="inline-flex w-fit items-center rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-violet-700"
        >
          <Icon name="plus" size={17} className="mr-2" />
          Add Tour
        </button>
      </div>

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {cards.map((card) => (
          <button
            key={card.label}
            type="button"
            onClick={() => changePage(card.action)}
            className="group rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md md:p-5"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  {card.label}
                </p>
                <p className="mt-2 text-2xl font-bold text-slate-900 md:text-[27px]">
                  {card.value}
                </p>
              </div>

              <span
                className={`flex h-9 w-9 items-center justify-center rounded-xl ${toneClasses[card.tone]}`}
              >
                <Icon name={card.icon} size={17} />
              </span>
            </div>

            <p className="mt-3 text-xs text-slate-400">{card.description}</p>
          </button>
        ))}
      </div>

      <div className="grid gap-5 xl:grid-cols-[1.5fr_0.8fr]">
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6">
          <div className="mb-5 flex items-center justify-between">
            <div>
              <span className="text-base font-bold text-slate-900">Quick Actions</span>
              <p className="mt-1 text-xs text-slate-400">Common administration tasks</p>
            </div>

            <span className="rounded-lg bg-slate-50 px-2.5 py-1 text-[10px] font-semibold text-slate-400">
              WORKSPACE
            </span>
          </div>

          <div className="grid gap-3 md:grid-cols-3">
            {[
              {
                title: 'Import Tour files',
                text: 'Upload multiple tours, images and PDFs.',
                icon: 'upload',
                tone: 'violet',
                page: 'import'
              },
              {
                title: 'Manage Tours',
                text: 'Create, update or remove tours.',
                icon: 'route',
                tone: 'amber',
                page: 'tours'
              },
              {
                title: 'Manage Users',
                text: 'Review roles and platform access.',
                icon: 'users',
                tone: 'emerald',
                page: 'users'
              }
            ].map((item) => (
              <button
                key={item.title}
                type="button"
                onClick={() => changePage(item.page)}
                className="group rounded-xl border border-slate-100 bg-slate-50/70 p-4 text-left transition hover:border-slate-200 hover:bg-white hover:shadow-sm"
              >
                <span
                  className={[
                    'flex h-10 w-10 items-center justify-center rounded-xl',
                    item.tone === 'violet' && 'bg-violet-50 text-violet-600',
                    item.tone === 'amber' && 'bg-amber-50 text-amber-600',
                    item.tone === 'emerald' && 'bg-emerald-50 text-emerald-600'
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <Icon name={item.icon} size={18} />
                </span>

                <span className="mt-4 text-sm font-semibold text-slate-800">
                  {item.title}
                </span>

                <p className="mt-1 min-h-[40px] text-xs leading-5 text-slate-400">
                  {item.text}
                </p>

                <span className="mt-3 inline-flex items-center text-xs font-semibold text-violet-600">
                  Open
                  <Icon
                    name="arrow"
                    size={13}
                    className="ml-1 transition group-hover:translate-x-0.5"
                  />
                </span>
              </button>
            ))}
          </div>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6">
          <div className="flex items-start justify-between">
            <div>
              <span className="text-base font-bold text-slate-900">System Status</span>
              <p className="mt-1 text-xs text-slate-400">Current platform health</p>
            </div>

            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
              <Icon name="shield" size={17} />
            </div>
          </div>

          <div className="mt-6 flex items-center rounded-xl bg-emerald-50/70 p-3.5">
            <span className="mr-3 flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <Icon name="check" size={16} />
            </span>

            <div>
              <p className="text-xs font-semibold text-emerald-800">
                All systems operational
              </p>
              <p className="mt-0.5 text-[11px] text-emerald-600">
                Firebase services connected
              </p>
            </div>
          </div>

          <div className="mt-5 divide-y divide-slate-100">
            {[
              ['Tours', stats.totalTours],
              ['Featured', stats.featuredTours],
              ['Users', stats.totalUsers],
              ['New messages', stats.newContacts]
            ].map(([label, value]) => (
              <div key={label} className="flex items-center justify-between py-3">
                <span className="text-xs text-slate-400">{label}</span>
                <span className="text-xs font-semibold text-slate-700">{value}</span>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Import page                                                                 */
/* -------------------------------------------------------------------------- */

function ImportPage({
  tours,
  toursLoading,
  selectedImages,
  selectedPdfs,
  handleImageSelect,
  handlePdfSelect,
  importAllTours,
  importLoading,
  progress,
  results
}) {
  const completed = results.success.length;
  const failed = results.errors.length;

  const pendingCount = tours.filter(
    (tour) => selectedImages[tour.id] || selectedPdfs[tour.id]
  ).length;

  const total = pendingCount || tours.length;
  const progressPercent = progress.total
    ? Math.round((progress.current / progress.total) * 100)
    : 0;

  return (
    <div>
      <div className="mb-6 flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
        <div>
          <p className="text-sm text-slate-400">Tour content</p>
          <span className="mt-1 text-2xl font-bold text-slate-900">
            Import Tour files
          </span>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">
            These are the tours currently in the database. Pick a new cover
            image or PDF for any tour and click Save — tours without a picked
            file are left untouched.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <span className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-500">
            {pendingCount > 0
              ? `${pendingCount} file${pendingCount === 1 ? '' : 's'} pending`
              : `${tours.length} tour${tours.length === 1 ? '' : 's'}`}
          </span>

          <button
            type="button"
            onClick={importAllTours}
            disabled={importLoading || pendingCount === 0}
            className="inline-flex items-center rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {importLoading ? (
              <>
                <span className="mr-2 h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Icon name="upload" size={16} className="mr-2" />
                Save Selected Files
              </>
            )}
          </button>
        </div>
      </div>

      {importLoading && (
        <div className="mb-5 rounded-2xl border border-violet-100 bg-violet-50 p-4">
          <div className="flex items-center justify-between text-xs">
            <span className="font-semibold text-violet-800">{progress.message}</span>
            <span className="font-bold text-violet-600">{progressPercent}%</span>
          </div>

          <div className="mt-3 h-2 overflow-hidden rounded-full bg-violet-100">
            <div
              className="h-full rounded-full bg-violet-600 transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      )}

      {toursLoading ? (
        <TableLoading text="Loading tours..." />
      ) : tours.length === 0 ? (
        <EmptyState
          icon="route"
          title="No tours yet"
          description="Add tours in the Manage Tours tab first, then come back here to attach files."
        />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {tours.map((tour, index) => {
            const storedPdfName = tour.itineraryPdf
              ? extractFilenameFromUrl(tour.itineraryPdf)
              : '';
            const storedCoverName = tour.coverImage
              ? extractFilenameFromUrl(tour.coverImage)
              : '';

            return (
              <div
                key={tour.id}
                className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
              >
                <div className="flex items-start justify-between border-b border-slate-100 p-5">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-lg bg-slate-100 px-2 py-1 text-[10px] font-bold text-slate-500">
                        TOUR {String(index + 1).padStart(2, '0')}
                      </span>

                      {tour.featured && (
                        <span className="inline-flex items-center rounded-lg bg-amber-50 px-2 py-1 text-[10px] font-semibold text-amber-600">
                          <Icon name="star" size={11} className="mr-1" />
                          Featured
                        </span>
                      )}
                    </div>

                    <span className="mt-3 text-sm font-bold leading-5 text-slate-900">
                      {tour.title}
                    </span>

                    <p className="mt-1 text-[10px] text-slate-400">
                      {tour.duration || '—'} · {formatCurrency(tour.price)}
                    </p>
                  </div>

                  <span className="ml-4 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-violet-50 text-violet-600">
                    <Icon name="route" size={17} />
                  </span>
                </div>

                <div className="grid gap-3 p-5 md:grid-cols-2">
                  <FilePicker
                    label="Cover image"
                    hint={
                      storedCoverName
                        ? `Stored: ${storedCoverName}`
                        : 'No image set'
                    }
                    accept="image/*"
                    file={selectedImages[tour.id]}
                    icon="image"
                    onChange={(file) => handleImageSelect(tour.id, file)}
                  />

                  <FilePicker
                    label="Itinerary PDF"
                    hint={
                      storedPdfName ? `Stored: ${storedPdfName}` : 'No PDF set'
                    }
                    accept=".pdf,application/pdf"
                    file={selectedPdfs[tour.id]}
                    icon="file"
                    onChange={(file) => handlePdfSelect(tour.id, file)}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {(completed > 0 || failed > 0) && (
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          {completed > 0 && (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
              <div className="flex items-center text-emerald-700">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100">
                  <Icon name="check" size={16} />
                </span>
                <div className="ml-3">
                  <p className="text-sm font-bold">{completed} tours updated</p>
                  <p className="text-xs text-emerald-600">
                    New files were saved to Firebase Storage.
                  </p>
                </div>
              </div>
            </div>
          )}

          {failed > 0 && (
            <div className="rounded-2xl border border-red-200 bg-red-50 p-5">
              <p className="text-sm font-bold text-red-700">
                {failed} uploads failed
              </p>

              <div className="mt-3 space-y-2">
                {results.errors.map((item) => (
                  <p key={item.title} className="text-xs text-red-600">
                    <strong>{item.title}:</strong> {item.error}
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FilePicker({ label, hint, accept, file, icon, onChange }) {
  return (
    <label className="group block cursor-pointer rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4 transition hover:border-violet-300 hover:bg-violet-50/40">
      <div className="flex items-center">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white text-slate-500 shadow-sm">
          <Icon name={icon} size={16} />
        </span>

        <div className="ml-3 min-w-0">
          <p className="text-xs font-semibold text-slate-700">{label}</p>
          <p className="truncate text-[10px] text-slate-400">{hint}</p>
        </div>
      </div>

      <div className="mt-3 rounded-lg bg-white px-3 py-2 text-xs text-slate-500 shadow-sm">
        {file ? (
          <span className="font-medium text-emerald-600">{file.name}</span>
        ) : (
          'Choose file'
        )}
      </div>

      <input
        type="file"
        accept={accept}
        className="hidden"
        onChange={(event) => onChange(event.target.files?.[0] || null)}
      />
    </label>
  );
}

/* -------------------------------------------------------------------------- */
/* Tours page                                                                  */
/* -------------------------------------------------------------------------- */

function ToursPage({
  tours,
  toursLoading,
  search,
  setSearch,
  showAddTourForm,
  setShowAddTourForm,
  newTour,
  setNewTour,
  newTourImage,
  setNewTourImage,
  newTourPdf,
  setNewTourPdf,
  handleAddTour,
  isAddingTour,
  deleteTour,
  tourMessage,
  uploadItineraryForTour,
  uploadingTourId,
  openEditTour
}) {
  return (
    <div>
      <div className="mb-5 flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
        <div>
          <p className="text-sm text-slate-400">Tour catalogue</p>
          <span className="mt-1 text-2xl font-bold text-slate-900">Manage Tours</span>
          <p className="mt-1 text-sm text-slate-500">
            Keep your safari catalogue accurate and up to date.
          </p>
        </div>

        <button
          type="button"
          onClick={() => setShowAddTourForm((value) => !value)}
          className="inline-flex w-fit items-center rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-violet-700"
        >
          <Icon name={showAddTourForm ? 'close' : 'plus'} size={16} className="mr-2" />
          {showAddTourForm ? 'Close Form' : 'Add New Tour'}
        </button>
      </div>

      {tourMessage && (
        <AlertMessage
          message={tourMessage}
          danger={tourMessage.toLowerCase().includes('error')}
        />
      )}

      {showAddTourForm && (
        <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6">
          <div className="mb-5 flex items-center justify-between">
            <div>
              <span className="text-base font-bold text-slate-900">Add New Tour</span>
              <p className="mt-1 text-xs text-slate-400">
                Enter the core details for the new safari experience.
              </p>
            </div>

            <span className="rounded-lg bg-violet-50 px-2.5 py-1 text-[10px] font-bold text-violet-600">
              NEW TOUR
            </span>
          </div>

          <form onSubmit={handleAddTour}>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <InputField
                label="Tour title"
                required
                className="md:col-span-2"
                value={newTour.title}
                placeholder="e.g. Classic Namibia"
                onChange={(value) => setNewTour({ ...newTour, title: value })}
              />

              <InputField
                label="Duration"
                required
                value={newTour.duration}
                placeholder="e.g. 7 Days"
                onChange={(value) => setNewTour({ ...newTour, duration: value })}
              />

              <InputField
                label="Price (N$)"
                required
                type="number"
                value={newTour.price}
                placeholder="8500"
                onChange={(value) => setNewTour({ ...newTour, price: value })}
              />

              <InputField
                label="Rating"
                type="number"
                step="0.1"
                min="0"
                max="5"
                value={newTour.rating}
                placeholder="4.5"
                onChange={(value) => setNewTour({ ...newTour, rating: value })}
              />

              <div className="md:col-span-2 lg:col-span-3">
                <label className="block text-xs font-semibold text-slate-600">
                  Description
                </label>

                <textarea
                  rows="4"
                  value={newTour.description}
                  onChange={(event) =>
                    setNewTour({ ...newTour, description: event.target.value })
                  }
                  placeholder="Describe the tour..."
                  className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 outline-none transition placeholder:text-slate-300 focus:border-violet-400 focus:ring-4 focus:ring-violet-50"
                />
              </div>

              <div className="rounded-xl bg-slate-50 p-4">
                <label className="flex cursor-pointer items-start">
                  <input
                    type="checkbox"
                    checked={newTour.featured}
                    onChange={(event) =>
                      setNewTour({ ...newTour, featured: event.target.checked })
                    }
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                  />

                  <span className="ml-3">
                    <span className="block text-xs font-semibold text-slate-700">
                      Featured tour
                    </span>
                    <span className="mt-1 block text-[11px] leading-5 text-slate-400">
                      Highlight this tour on the public website.
                    </span>
                  </span>
                </label>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600">
                  Cover image
                </label>

                <label className="mt-2 flex cursor-pointer items-center rounded-xl border border-dashed border-slate-200 bg-slate-50 px-3 py-3 text-xs text-slate-500 hover:border-violet-300">
                  <Icon name="image" size={16} className="mr-2" />
                  <span className="truncate">
                    {newTourImage?.name || 'Choose image'}
                  </span>

                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(event) =>
                      setNewTourImage(event.target.files?.[0] || null)
                    }
                  />
                </label>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600">
                  Itinerary PDF
                </label>

                <label className="mt-2 flex cursor-pointer items-center rounded-xl border border-dashed border-slate-200 bg-slate-50 px-3 py-3 text-xs text-slate-500 hover:border-violet-300">
                  <Icon name="file" size={16} className="mr-2" />
                  <span className="truncate">
                    {newTourPdf?.name || 'Choose PDF'}
                  </span>

                  <input
                    type="file"
                    accept=".pdf,application/pdf"
                    className="hidden"
                    onChange={(event) =>
                      setNewTourPdf(event.target.files?.[0] || null)
                    }
                  />
                </label>
              </div>

              <div className="flex items-end">
                <button
                  type="submit"
                  disabled={isAddingTour}
                  className="inline-flex w-full items-center justify-center rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-700 disabled:opacity-60"
                >
                  {isAddingTour ? (
                    <>
                      <span className="mr-2 h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <Icon name="plus" size={16} className="mr-2" />
                      Add Tour
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-slate-100 p-4 md:flex-row md:items-center md:justify-between md:p-5">
          <div>
            <span className="text-sm font-bold text-slate-900">Tour Catalogue</span>
            <p className="mt-1 text-xs text-slate-400">
              {tours.length} tour{tours.length === 1 ? '' : 's'} shown
            </p>
          </div>

          <div className="relative w-full md:w-[280px]">
            <Icon
              name="search"
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />

            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search tours..."
              className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-3 text-xs outline-none focus:border-violet-400 focus:bg-white focus:ring-4 focus:ring-violet-50"
            />
          </div>
        </div>

        {toursLoading ? (
          <TableLoading text="Loading tours..." />
        ) : tours.length === 0 ? (
          <EmptyState
            icon="route"
            title="No tours found"
            description="Add your first tour or adjust your search."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[920px]">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/70">
                  <th className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-wide text-slate-400">
                    Tour
                  </th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wide text-slate-400">
                    Duration
                  </th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wide text-slate-400">
                    Price
                  </th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wide text-slate-400">
                    Rating
                  </th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wide text-slate-400">
                    Itinerary
                  </th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wide text-slate-400">
                    Status
                  </th>
                  <th className="px-5 py-3 text-right text-[10px] font-bold uppercase tracking-wide text-slate-400">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {tours.map((tour) => {
                  const isUploading = uploadingTourId === tour.id;
                  const hasPdf = Boolean(tour.itineraryPdf);
                  const pdfName = tour.itineraryPdf
                    ? extractFilenameFromUrl(tour.itineraryPdf)
                    : '';

                  return (
                    <tr
                      key={tour.id}
                      className="border-b border-slate-50 transition hover:bg-slate-50/60"
                    >
                      <td className="px-5 py-3.5">
                        <div className="flex items-center">
                          {tour.coverImage ? (
                            <img
                              src={tour.coverImage}
                              alt={tour.title}
                              className="h-10 w-12 rounded-lg object-cover"
                              onError={(event) => {
                                event.currentTarget.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="flex h-10 w-12 items-center justify-center rounded-lg bg-slate-100 text-slate-400">
                              <Icon name="image" size={15} />
                            </div>
                          )}

                          <div className="ml-3 min-w-0">
                            <p className="max-w-[220px] truncate text-xs font-semibold text-slate-800">
                              {tour.title}
                            </p>
                            <p className="mt-0.5 text-[10px] text-slate-400">
                              Updated {formatDate(tour.updatedAt)}
                            </p>
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-3.5 text-xs text-slate-500">
                        {tour.duration || '—'}
                      </td>

                      <td className="px-4 py-3.5 text-xs font-semibold text-slate-700">
                        {formatCurrency(tour.price)}
                      </td>

                      <td className="px-4 py-3.5">
                        <span className="inline-flex items-center rounded-lg bg-amber-50 px-2 py-1 text-[10px] font-semibold text-amber-600">
                          <Icon name="star" size={10} className="mr-1" />
                          {tour.rating || '—'}
                        </span>
                      </td>

                      <td className="px-4 py-3.5">
                        {hasPdf ? (
                          <div className="flex items-center">
                            <span className="inline-flex items-center rounded-lg bg-emerald-50 px-2 py-1 text-[10px] font-semibold text-emerald-600">
                              <Icon name="check" size={10} className="mr-1" />
                              Stored
                            </span>
                            <span
                              className="ml-2 max-w-[130px] truncate text-[10px] text-slate-400"
                              title={pdfName}
                            >
                              {pdfName}
                            </span>
                          </div>
                        ) : (
                          <span className="rounded-lg bg-slate-100 px-2 py-1 text-[10px] font-semibold text-slate-500">
                            No PDF
                          </span>
                        )}
                      </td>

                      <td className="px-4 py-3.5">
                        {tour.featured ? (
                          <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-semibold text-emerald-600">
                            Featured
                          </span>
                        ) : (
                          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-semibold text-slate-500">
                            Standard
                          </span>
                        )}
                      </td>

                      <td className="px-5 py-3.5">
                        <div className="flex justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => openEditTour(tour)}
                            className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                            title="Edit tour"
                          >
                            <Icon name="edit" size={15} />
                          </button>

                          <label
                            title={hasPdf ? 'Replace PDF' : 'Upload PDF'}
                            className={[
                              'inline-flex cursor-pointer items-center rounded-lg px-2.5 py-2 text-xs font-medium transition',
                              isUploading
                                ? 'cursor-wait bg-violet-50 text-violet-500'
                                : 'text-slate-500 hover:bg-violet-50 hover:text-violet-600'
                            ].join(' ')}
                          >
                            {isUploading ? (
                              <>
                                <span className="mr-1.5 h-3.5 w-3.5 rounded-full border-2 border-violet-200 border-t-violet-500 animate-spin" />
                                <span className="hidden sm:inline">Uploading…</span>
                              </>
                            ) : (
                              <>
                                <Icon name="upload" size={15} />
                                <span className="ml-1.5 hidden sm:inline">
                                  {hasPdf ? 'Replace' : 'Upload'}
                                </span>
                              </>
                            )}

                            <input
                              type="file"
                              accept=".pdf,application/pdf"
                              className="hidden"
                              disabled={isUploading}
                              onChange={(event) => {
                                const file = event.target.files?.[0];
                                if (file) uploadItineraryForTour(tour.id, file);
                                event.target.value = '';
                              }}
                            />
                          </label>

                          <button
                            type="button"
                            onClick={() => deleteTour(tour.id)}
                            className="rounded-lg p-2 text-slate-400 transition hover:bg-red-50 hover:text-red-500"
                            title="Delete tour"
                          >
                            <Icon name="trash" size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Edit tour modal                                                             */
/* -------------------------------------------------------------------------- */

function EditTourModal({
  editingTour,
  setEditingTour,
  editTourImage,
  setEditTourImage,
  editTourPdf,
  setEditTourPdf,
  handleUpdateTour,
  isSavingEdit,
  closeEditTour
}) {
  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-slate-950/50 p-4 md:p-8"
      onClick={closeEditTour}
    >
      <div
        className="w-full max-w-2xl rounded-2xl bg-white shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-slate-100 p-5 md:p-6">
          <div className="min-w-0">
            <span className="text-base font-bold text-slate-900">Edit Tour</span>
            <p className="mt-1 truncate text-xs text-slate-400">
              {editingTour.title}
            </p>
          </div>

          <button
            type="button"
            onClick={closeEditTour}
            disabled={isSavingEdit}
            className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:opacity-60"
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        <form onSubmit={handleUpdateTour}>
          <div className="grid gap-4 p-5 md:p-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <InputField
                  label="Tour title"
                  required
                  value={editingTour.title}
                  placeholder="e.g. Classic Namibia"
                  onChange={(value) =>
                    setEditingTour({ ...editingTour, title: value })
                  }
                />
              </div>

              <InputField
                label="Duration"
                required
                value={editingTour.duration}
                placeholder="e.g. 7 Days"
                onChange={(value) =>
                  setEditingTour({ ...editingTour, duration: value })
                }
              />

              <InputField
                label="Price (N$)"
                required
                type="number"
                value={editingTour.price}
                placeholder="8500"
                onChange={(value) =>
                  setEditingTour({ ...editingTour, price: value })
                }
              />

              <InputField
                label="Rating"
                type="number"
                step="0.1"
                min="0"
                max="5"
                value={editingTour.rating}
                placeholder="4.5"
                onChange={(value) =>
                  setEditingTour({ ...editingTour, rating: value })
                }
              />

              <div className="flex items-end">
                <label className="flex cursor-pointer items-start rounded-xl bg-slate-50 p-3">
                  <input
                    type="checkbox"
                    checked={editingTour.featured}
                    onChange={(event) =>
                      setEditingTour({
                        ...editingTour,
                        featured: event.target.checked
                      })
                    }
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                  />

                  <span className="ml-3">
                    <span className="block text-xs font-semibold text-slate-700">
                      Featured tour
                    </span>
                    <span className="mt-0.5 block text-[10px] text-slate-400">
                      Highlight on the public website.
                    </span>
                  </span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600">
                Description
              </label>

              <textarea
                rows="4"
                value={editingTour.description}
                onChange={(event) =>
                  setEditingTour({
                    ...editingTour,
                    description: event.target.value
                  })
                }
                placeholder="Describe the tour..."
                className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 outline-none transition placeholder:text-slate-300 focus:border-violet-400 focus:ring-4 focus:ring-violet-50"
              />
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <label className="block text-xs font-semibold text-slate-600">
                  Cover image
                </label>

                <label className="mt-2 flex cursor-pointer items-center rounded-xl border border-dashed border-slate-200 bg-slate-50 px-3 py-3 text-xs text-slate-500 hover:border-violet-300">
                  <Icon name="image" size={16} className="mr-2 shrink-0" />
                  <span className="truncate">
                    {editTourImage?.name ||
                      (editingTour.coverImage
                        ? `Current: ${extractFilenameFromUrl(editingTour.coverImage)}`
                        : 'Choose image')}
                  </span>

                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(event) =>
                      setEditTourImage(event.target.files?.[0] || null)
                    }
                  />
                </label>

                <p className="mt-1.5 text-[10px] text-slate-400">
                  Leave empty to keep the current image.
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600">
                  Itinerary PDF
                </label>

                <label className="mt-2 flex cursor-pointer items-center rounded-xl border border-dashed border-slate-200 bg-slate-50 px-3 py-3 text-xs text-slate-500 hover:border-violet-300">
                  <Icon name="file" size={16} className="mr-2 shrink-0" />
                  <span className="truncate">
                    {editTourPdf?.name ||
                      (editingTour.itineraryPdf
                        ? `Current: ${extractFilenameFromUrl(editingTour.itineraryPdf)}`
                        : 'Choose PDF')}
                  </span>

                  <input
                    type="file"
                    accept=".pdf,application/pdf"
                    className="hidden"
                    onChange={(event) =>
                      setEditTourPdf(event.target.files?.[0] || null)
                    }
                  />
                </label>

                <p className="mt-1.5 text-[10px] text-slate-400">
                  Leave empty to keep the current PDF.
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-100 p-4 md:p-5">
            <button
              type="button"
              onClick={closeEditTour}
              disabled={isSavingEdit}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={isSavingEdit}
              className="inline-flex items-center justify-center rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-700 disabled:opacity-60"
            >
              {isSavingEdit ? (
                <>
                  <span className="mr-2 h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Icon name="check" size={16} className="mr-2" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Users page                                                                  */
/* -------------------------------------------------------------------------- */

function UsersPage({
  users,
  usersLoading,
  search,
  setSearch,
  toggleAdminRole,
  deleteUser,
  userMessage
}) {
  return (
    <div>
      <div className="mb-5 flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
        <div>
          <p className="text-sm text-slate-400">Access management</p>
          <span className="mt-1 text-2xl font-bold text-slate-900">Manage Users</span>
          <p className="mt-1 text-sm text-slate-500">
            Manage platform accounts and administrator permissions.
          </p>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs font-semibold text-slate-500">
          {users.length} user{users.length === 1 ? '' : 's'}
        </div>
      </div>

      {userMessage && (
        <AlertMessage
          message={userMessage}
          danger={userMessage.toLowerCase().includes('error')}
        />
      )}

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-slate-100 p-4 md:flex-row md:items-center md:justify-between md:p-5">
          <div>
            <span className="text-sm font-bold text-slate-900">User Directory</span>
            <p className="mt-1 text-xs text-slate-400">
              Search and manage registered accounts.
            </p>
          </div>

          <div className="relative w-full md:w-[280px]">
            <Icon
              name="search"
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />

            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search users..."
              className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-3 text-xs outline-none focus:border-violet-400 focus:bg-white focus:ring-4 focus:ring-violet-50"
            />
          </div>
        </div>

        {usersLoading ? (
          <TableLoading text="Loading users..." />
        ) : users.length === 0 ? (
          <EmptyState
            icon="users"
            title="No users found"
            description="There are no users matching your search."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px]">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/70">
                  <th className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-wide text-slate-400">
                    User
                  </th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wide text-slate-400">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wide text-slate-400">
                    Role
                  </th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wide text-slate-400">
                    Last Login
                  </th>
                  <th className="px-5 py-3 text-right text-[10px] font-bold uppercase tracking-wide text-slate-400">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {users.map((item) => (
                  <tr
                    key={item.uid || item.id}
                    className="border-b border-slate-50 transition hover:bg-slate-50/60"
                  >
                    <td className="px-5 py-3.5">
                      <div className="flex items-center">
                        {item.photoURL ? (
                          <img
                            src={item.photoURL}
                            alt={item.displayName || 'User'}
                            className="h-9 w-9 rounded-full object-cover"
                          />
                        ) : (
                          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-violet-100 text-xs font-bold text-violet-700">
                            {(item.displayName || item.email || 'U')
                              .charAt(0)
                              .toUpperCase()}
                          </div>
                        )}

                        <div className="ml-3 min-w-0">
                          <p className="max-w-[190px] truncate text-xs font-semibold text-slate-800">
                            {item.displayName || 'No Name'}
                          </p>
                          <p className="mt-0.5 text-[10px] text-slate-400">
                            {item.uid ? `ID: ${item.uid.slice(0, 8)}...` : 'User account'}
                          </p>
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-3.5 text-xs text-slate-500">
                      {item.email || '—'}
                    </td>

                    <td className="px-4 py-3.5">
                      {item.role === 'admin' ? (
                        <span className="inline-flex items-center rounded-full bg-violet-50 px-2.5 py-1 text-[10px] font-semibold text-violet-700">
                          Administrator
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-semibold text-slate-500">
                          User
                        </span>
                      )}
                    </td>

                    <td className="px-4 py-3.5 text-xs text-slate-500">
                      {formatDate(item.lastLogin)}
                    </td>

                    <td className="px-5 py-3.5">
                      <div className="flex justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => toggleAdminRole(item.uid, item.role)}
                          className="rounded-lg p-2 text-slate-400 transition hover:bg-violet-50 hover:text-violet-600"
                          title={
                            item.role === 'admin'
                              ? 'Remove administrator'
                              : 'Make administrator'
                          }
                        >
                          <Icon name="shield" size={15} />
                        </button>

                        <button
                          type="button"
                          onClick={() => deleteUser(item.uid)}
                          className="rounded-lg p-2 text-slate-400 transition hover:bg-red-50 hover:text-red-500"
                          title="Delete user"
                        >
                          <Icon name="trash" size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Small reusable UI                                                           */
/* -------------------------------------------------------------------------- */

function InputField({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  required = false,
  step,
  min,
  max,
  className = ''
}) {
  return (
    <div className={className}>
      <label className="block text-xs font-semibold text-slate-600">
        {label}
        {required && <span className="ml-1 text-violet-600">*</span>}
      </label>

      <input
        type={type}
        required={required}
        step={step}
        min={min}
        max={max}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 outline-none transition placeholder:text-slate-300 focus:border-violet-400 focus:ring-4 focus:ring-violet-50"
      />
    </div>
  );
}

function AlertMessage({ message, danger = false }) {
  return (
    <div
      className={[
        'mb-5 flex items-start rounded-xl border px-4 py-3 text-sm',
        danger
          ? 'border-red-100 bg-red-50 text-red-700'
          : 'border-emerald-100 bg-emerald-50 text-emerald-700'
      ].join(' ')}
    >
      <span className="mr-2 mt-0.5">
        <Icon name={danger ? 'close' : 'check'} size={15} />
      </span>
      <span>{message}</span>
    </div>
  );
}

function TableLoading({ text }) {
  return (
    <div className="flex min-h-[280px] flex-col items-center justify-center">
      <div className="h-8 w-8 rounded-full border-4 border-slate-200 border-t-violet-600 animate-spin" />
      <p className="mt-4 text-xs font-medium text-slate-500">{text}</p>
    </div>
  );
}

function EmptyState({ icon, title, description }) {
  return (
    <div className="flex min-h-[280px] flex-col items-center justify-center px-5 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
        <Icon name={icon} size={20} />
      </div>

      <span className="mt-4 text-sm font-bold text-slate-800">{title}</span>

      <p className="mt-1 max-w-sm text-xs leading-5 text-slate-400">
        {description}
      </p>
    </div>
  );
}

export default ImportTours;